// A página do Mentor no app — /mentor/:slug (Círculo, P1, 14/10/2026).
//
// O desenho é o protótipo aprovado (docs/circulo-mentor/prototipo/
// circulo-do-mentor-piloto.html, ?nav=final; regras em 05-variante-final.md):
// mesma marcação e mesmas classes, e o CSS é copiado de lá por
// scripts/circulo-css-do-prototipo.js (mentor-pagina.css, preso em .cm).
//
// Ordem: topo compacto (nome, bio, números, botão) → mapa de abertura (só quem
// não assina, até tocar num cartão; "visto" por conta em
// profiles.tutorials_state.circulo_mapa) → abas presas → a aba. Conteúdo é
// "Na mesa agora" + a série atual; Clube, Estudo e Cadernos (P2, 15/10/2026)
// estão em mentor-abas.tsx e só buscam os dados quando a aba é aberta.
//
// A aba fica no endereço (?aba=clube; P2b): o link abre direto nela e o voltar
// do navegador troca de aba. Ao trocar, a página rola até o começo da aba,
// logo abaixo das abas presas.
//
// Tudo vem da API (server/circulo/routes.js): números do ano, onde o Mentor
// está no livro, estado de cada episódio para quem pede, link assinado do
// vídeo e o progresso salvo.
//
// O MENTOR ESCOLHE (Editor, parte 1, 15/10/2026): perfil.pagina traz o modelo,
// a paleta e as seções na ordem dele (server/circulo/editor.js). As abas e o
// mapa seguem as seções ligadas; modelo e paleta vão na raiz (data-layout,
// data-paleta) e mentor-pagina-modelos.css os aplica (parte 3): Editorial é a
// de sempre, Estante põe as séries em lombadas logo abaixo do topo, Diário
// escreve os títulos à mão. Sem nada escolhido, a página é a de sempre.
//
// Sem conta (P4, 15/10/2026), a página abre em modo VITRINE: tudo vem de uma
// rota pública só de leitura (server/circulo/vitrine.js), nenhum vídeo toca e
// Assinar / Entrar no clube / Criar caderno chamam onEntrar — o App leva ao
// cadastro ou login e volta para cá com abrirAoEntrar (o passo seguinte).
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../../services/apiV2';
import { usoAcao } from '../uso';
import { AssinarMentor } from './assinar-mentor';
import { isNativeApp } from '../platform';
import { AbaCadernos, AbaClube, AbaEstudo } from './mentor-abas';
import './mentor-pagina.css';
import './mentor-pagina-app.css';
import './mentor-pagina-modelos.css';

type Aba = 'conteudo' | 'clube' | 'estudo' | 'cadernos';
// 'pagina' = o "Entrar" do topo: só volta para a página.
export type AcaoAoEntrar = 'pagina' | 'assinar' | 'clube' | 'caderno';
const ABAS: [Aba, string][] = [['conteudo', 'Conteúdo'], ['clube', 'Clube'], ['estudo', 'Estudo'], ['cadernos', 'Cadernos']];
// As seções da página na ordem do Mentor; sem escolha (ou chave que falta), a de sempre.
type Secao = { key: string; on: boolean };
const SECOES_PADRAO: Secao[] = ['mapa', 'conteudo', 'clube', 'estudo', 'cadernos', 'sobre'].map((key) => ({ key, on: key !== 'sobre' }));
const secoesDe = (lista?: Secao[] | null): Secao[] => {
  const out = (Array.isArray(lista) ? lista : []).filter((s) => SECOES_PADRAO.some((p) => p.key === s?.key));
  return [...out, ...SECOES_PADRAO.filter((p) => !out.some((s) => s.key === p.key))];
};
const NUM_LUGARES = ['', '', 'Dois', 'Três', 'Quatro'];
// null = endereço sem ?aba: a primeira aba que o Mentor deixou ligada.
const abaDaBusca = (search: string): Aba | null => {
  const a = new URLSearchParams(search).get('aba');
  return ABAS.some(([k]) => k === a) ? (a as Aba) : null;
};
const ROM = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'XVIII', 'XIX', 'XX'];
const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
// A capa da série usa a paleta do protótipo (a do livro do Munger).
const CAPA = { bg: '#1E2A1C', fg: '#EDE6D2', ac: '#8FA873' };
const capaVars = { '--bg': CAPA.bg, '--fg': CAPA.fg, '--ac': CAPA.ac } as React.CSSProperties;

/* ===== ícones do protótipo ===== */
const Ic = {
  play: <svg viewBox="0 0 24 24"><path d="M7 4.6v14.8L19.8 12z" fill="currentColor" /></svg>,
  left: <svg viewBox="0 0 24 24"><path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  right: <svg viewBox="0 0 24 24" width="16" height="16"><path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  x: <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" /></svg>,
  lock: <svg viewBox="0 0 24 24"><rect x="5" y="10.5" width="14" height="10" rx="2.2" fill="currentColor" /><path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" stroke="currentColor" strokeWidth="2" fill="none" /></svg>,
  lockP: <svg viewBox="0 0 24 24" width="13" height="13"><rect x="5" y="10.5" width="14" height="10" rx="2.2" fill="currentColor" /><path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" stroke="currentColor" strokeWidth="2" fill="none" /></svg>,
  check: <svg viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  book: <svg viewBox="0 0 24 24"><path d="M5 4.5h9.5A3.5 3.5 0 0 1 18 8v11.5H8.5A3.5 3.5 0 0 1 5 16z M5 16a3.5 3.5 0 0 1 3.5-3.5H18" stroke="currentColor" strokeWidth="1.7" fill="none" strokeLinejoin="round" /></svg>,
  users: <svg viewBox="0 0 24 24"><circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.8" fill="none" /><path d="M3.5 19.5c0-3 2.5-5 5.5-5s5.5 2 5.5 5" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" /><circle cx="17" cy="9" r="2.4" stroke="currentColor" strokeWidth="1.6" fill="none" /><path d="M14.8 14.2c2.4.3 4.2 2 4.7 4.3" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" /></svg>,
  layers: <svg viewBox="0 0 24 24"><path d="M12 4l8 4.5-8 4.5-8-4.5z" stroke="currentColor" strokeWidth="1.7" fill="none" strokeLinejoin="round" /><path d="M4 13l8 4.5 8-4.5" stroke="currentColor" strokeWidth="1.7" fill="none" strokeLinejoin="round" strokeLinecap="round" /></svg>,
  pencil: <svg viewBox="0 0 24 24"><path d="M15.5 4.5l4 4L8 20H4v-4z" stroke="currentColor" strokeWidth="1.7" fill="none" strokeLinejoin="round" strokeLinecap="round" /></svg>,
};

const fmtN = (n: number) => (n || 0).toLocaleString('pt-BR');
// Abaixo de 1 min, em segundos ("40 s"), como o player mostra.
const minutos = (s?: number | null) => (!s ? '' : s < 59.5 ? `${Math.max(1, Math.round(s))} s` : `${Math.round(s / 60)} min`);
const primeiroNome = (nome: string) => String(nome || '').trim().split(/\s+/)[0] || '';
const normTitulo = (t?: string | null) => String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

/* ===== estado de um episódio para quem vê (epState do protótipo) ===== */
interface Ep {
  id: string; posicao: number; titulo: string; sinopse: string | null; abre_na_pagina: number;
  duracao_s: number | null; estreia_em: string | null; estado: string; texto: string; aberto: boolean;
  segundos_assistidos: number; tem_video: boolean; pode_assistir: boolean;
}
interface EstadoEp { cls: string; txt: string; st: string; open: boolean }
function epState(e: Ep, temAcesso: boolean): EstadoEp {
  if (e.estado === 'em_producao') return { cls: 'prod', txt: 'em produção', st: '', open: false };
  if (e.estado === 'estreia') return { cls: 'soon', txt: e.texto, st: 'next', open: false };
  if (!temAcesso) return { cls: 'lock', txt: 'assine para destrancar', st: '', open: false };
  if (e.estado === 'assistido') return { cls: 'seen', txt: 'assistido', st: 'ok', open: true };
  if (e.estado === 'liberado') return { cls: 'open', txt: e.texto, st: 'go', open: true };
  return { cls: 'lock', txt: `abre quando você chegar na p. ${e.abre_na_pagina}`, st: '', open: false };
}
const publicado = (e: Ep) => e.estado !== 'em_producao' && e.estado !== 'estreia';
const pctVisto = (e: Ep) => (e.duracao_s && e.segundos_assistidos ? Math.min(100, Math.round(e.segundos_assistidos / e.duracao_s * 100)) : 0);
// O próximo para a pessoa: o primeiro aberto e não assistido (senão o primeiro aberto).
const proximoEp = (eps: Ep[]) => eps.find((e) => e.pode_assistir && e.estado !== 'assistido') || eps.find((e) => e.pode_assistir) || null;

/* ===== peças visuais do protótipo ===== */
// Com a foto do exemplar que o Mentor enviou (editor, aba Série), a capa é a
// foto; sem ela, a arte desenhada de sempre.
const Capa: React.FC<{ titulo: string; autor: string; foto?: string | null }> = ({ titulo, autor, foto }) => (foto
  ? <div className="cover has-foto" data-capa-foto=""><img src={foto} alt={`Capa de ${titulo}`} loading="lazy" /></div>
  : <div className="cover v-frame" style={capaVars}><span className="cv-a">{autor}</span><span className="cv-o" /><span className="cv-t">{titulo}</span></div>
);
const Livro3d: React.FC<{ titulo: string; autor: string; paginas: number; w: string; foto?: string | null }> = ({ titulo, autor, paginas, w, foto }) => (
  <div>
    <div className="b3d" style={{ ...capaVars, '--w': w, '--k': (0.07 + Math.min(paginas || 0, 1100) / 9000).toFixed(3) } as React.CSSProperties}>
      <div className="b3-back" /><div className="b3-pages" />
      <div className="b3-spine"><span>{titulo}</span></div>
      <div className="b3-front"><Capa titulo={titulo} autor={autor} foto={foto} /></div>
    </div>
    <div className="b3-shadow" />
  </div>
);
const Quadro: React.FC<{ livro: { titulo: string; autor: string; foto?: string | null }; roman?: string; eye: string; title: string; dur?: string; prog?: number; dim?: boolean }> =
  ({ livro, roman, eye, title, dur, prog, dim }) => (
    <div className={`frame ${dim ? 'dim' : ''} ${roman ? 'has-roman' : ''}`} style={capaVars}>
      <div className="fr-cover"><Capa titulo={livro.titulo} autor={livro.autor} foto={livro.foto} /></div>
      {roman && <span className="fr-roman">{roman}</span>}
      <div className="fr-txt"><span className="fr-eye">{eye}</span><span className="fr-title">{title}</span></div>
      {dur && <span className="fr-dur">{dur}</span>}
      {prog != null && prog > 0 && <span className="pbar"><i style={{ width: `${prog}%` }} /></span>}
    </div>
  );

/* ===== a página ===== */
export const MentorPagina: React.FC<{
  session: any;
  slug: string;
  onBack: () => void;
  onAbrirCadernos?: () => void;
  onAbrirEstudio?: () => void;
  onAbrirEditor?: () => void;
  // O editor já na aba Série (o atalho "Publicar a série" do Mentor sem série publicada).
  onAbrirSerieNoEditor?: () => void;
  onAbrirClube?: (clubId: string) => void;
  onAbrirEstudoDoClube?: (clubId: string, bookId: string | null) => void;
  onAbrirClubes?: () => void;
  onAbrirCadernoDoLivro?: (bookId: string) => void;
  // Vitrine (sem conta): leva ao cadastro ('register') ou ao login, guardando o que a pessoa queria.
  // O nome do Mentor vai junto: o cadastro fala do Círculo dele (QA #9).
  onEntrar?: (acao: AcaoAoEntrar, modo: 'register' | 'login', mentorNome?: string) => void;
  // Voltou do cadastro/login: o passo que ficou pendente (abre uma vez).
  abrirAoEntrar?: AcaoAoEntrar | null;
  onAbriuAoEntrar?: () => void;
  // Vitrine aberta de verdade (o Mentor existe): o App guarda de onde a pessoa
  // veio, para o 1º login ou cadastro voltar a esta página.
  onVitrineCarregou?: (mentorNome?: string) => void;
}> = ({ session, slug, onBack, onAbrirCadernos, onAbrirEstudio, onAbrirEditor, onAbrirSerieNoEditor, onAbrirClube, onAbrirEstudoDoClube, onAbrirClubes, onAbrirCadernoDoLivro,
  onEntrar, abrirAoEntrar, onAbriuAoEntrar, onVitrineCarregou }) => {
  const vitrine = !session;
  const location = useLocation();
  const navigate = useNavigate();
  const abaPedida = abaDaBusca(location.search);
  // Quem chega por um link com ?aba= vai a um lugar certo: o mapa não fica no caminho.
  const [veioComAba] = useState(() => abaPedida !== null);
  const [perfil, setPerfil] = useState<any>(null);
  // PRÉVIA (Editor, parte 3): o editor abre esta página num iframe com ?previa=1
  // e manda o modelo, a paleta e as seções do rascunho por postMessage. Aqui ela
  // se mostra como o assinante vê, antes de o Mentor salvar. Só aceita mensagem
  // da janela de cima e da mesma origem.
  const [previa] = useState(() => new URLSearchParams(location.search).has('previa') && window.parent !== window);
  const [paginaPrevia, setPaginaPrevia] = useState<any>(null);
  useEffect(() => {
    if (!previa) return;
    const ouvir = (e: MessageEvent) => {
      if (e.source !== window.parent || e.origin !== window.location.origin) return;
      if (e.data?.tipo === 'circulo-previa' && e.data.pagina) setPaginaPrevia(e.data.pagina);
    };
    window.addEventListener('message', ouvir);
    window.parent.postMessage({ tipo: 'circulo-previa-pronta' }, window.location.origin);
    return () => window.removeEventListener('message', ouvir);
  }, [previa]);
  const pagina = paginaPrevia || perfil?.pagina || null;
  // Rastro de uso (v2/uso.ts; a prévia do editor não conta).
  useEffect(() => { if (!previa) usoAcao('mentor_abrir', { slug }); }, [slug, previa]);
  // Só as abas que o Mentor deixou ligadas, na ordem dele; a pedida que está
  // desligada cai na primeira ligada.
  const secoes = useMemo(() => secoesDe(pagina?.secoes), [pagina]);
  const abasVisiveis = useMemo(() => secoes.filter((s) => s.on)
    .map((s) => ABAS.find(([k]) => k === s.key)).filter(Boolean) as [Aba, string][], [secoes]);
  const temAba = (a: Aba) => abasVisiveis.some(([k]) => k === a);
  const abaInicial: Aba = abasVisiveis[0]?.[0] || 'conteudo';
  const aba: Aba = abaPedida && temAba(abaPedida) ? abaPedida : abaInicial;
  const [serie, setSerie] = useState<any>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [mapaVisto, setMapaVisto] = useState(true);
  const [folha, setFolha] = useState<null | { tipo: 'assinar' } | { tipo: 'serie' } | { tipo: 'ep'; id: string }>(null);
  // Rastro de uso: cada vez que a folha de assinar abre (o botão, um episódio fechado ou a volta do cadastro).
  useEffect(() => { if (folha?.tipo === 'assinar') usoAcao('assinar_clicar', { slug }); }, [folha?.tipo, slug]);
  const raizRef = useRef<HTMLDivElement>(null);
  const topoRef = useRef<HTMLElement>(null);
  const abasRef = useRef<HTMLDivElement>(null);
  const painelRef = useRef<HTMLDivElement>(null);
  const reguaRef = useRef<HTMLDivElement>(null);
  const [reguaRola, setReguaRola] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // Dados das abas Clube/Estudo (a mesma rota) e Cadernos: undefined = ainda não buscou.
  const [clube, setClube] = useState<any>(undefined);
  const [clubeErro, setClubeErro] = useState(false);
  const [caderno, setCaderno] = useState<any>(undefined);
  const [cadernoErro, setCadernoErro] = useState(false);

  const carregar = useCallback(async () => {
    try {
      if (!session) {
        // Vitrine: a mesma forma das rotas com login, numa chamada só e sem nada privado.
        const v = await api.getMentorVitrine(slug);
        if (v && v.disponivel === false) { setErro('O Círculo ainda não está disponível.'); return; }
        setPerfil(v);
        setMapaVisto(false);
        setSerie(v.serie || null);
        setClube(v.clube || null);
        setErro(null);
        onVitrineCarregou?.(v.mentor?.nome);
        return;
      }
      const p = await api.getMentor(session, slug);
      if (p && p.disponivel === false) { setErro('O Círculo ainda não está disponível.'); return; }
      setPerfil(p);
      setMapaVisto(!!p.mapa_visto);
      setSerie(p.serie_atual ? await api.getMentorSerie(session, slug, p.serie_atual.id) : null);
      setErro(null);
    } catch (e: any) {
      setErro(e?.status === 404 ? 'Este Círculo não existe.' : 'Não deu para abrir o Círculo agora. Tente de novo.');
    }
  }, [session, slug]);
  useEffect(() => { carregar(); }, [carregar]);

  const carregarClube = useCallback(async () => {
    setClubeErro(false);
    if (!session) { carregar(); return; }
    try { setClube(await api.getMentorClube(session, slug)); } catch { setClubeErro(true); }
  }, [session, slug, carregar]);
  const carregarCaderno = useCallback(async () => {
    setCadernoErro(false);
    try { setCaderno(await api.getMentorCaderno(session, slug)); } catch { setCadernoErro(true); }
  }, [session, slug]);
  // Com conta, o clube vem logo (não só na aba): o mapa e a folha só falam em
  // clube grátis se ele existir (QA #2). Na vitrine ele já vem com a página.
  useEffect(() => {
    if (session && clube === undefined && !clubeErro) carregarClube();
    if (aba === 'cadernos' && caderno === undefined && !cadernoErro && session) carregarCaderno();
  }, [aba, clube, clubeErro, caderno, cadernoErro, session, carregarClube, carregarCaderno]);

  // A régua rola sozinha até as bandeiras (o Mentor e você): no celular ela é
  // mais larga que a tela, e a bandeira do Mentor ficava cortada na borda. Se
  // as duas não cabem juntas, vale a do Mentor inteira (o resto se arrasta).
  useLayoutEffect(() => {
    const wrap = reguaRef.current;
    if (!wrap || aba !== 'conteudo') return;
    const caixa = wrap.getBoundingClientRect();
    const pos = (sel: string) => {
      const el = wrap.querySelector<HTMLElement>(`${sel} span`);
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return { l: r.left - caixa.left + wrap.scrollLeft - 12, r: r.right - caixa.left + wrap.scrollLeft + 12 };
    };
    // "Arraste para o lado" só quando a régua passa da tela (QA #13).
    setReguaRola(wrap.scrollWidth > wrap.clientWidth + 1);
    const men = pos('.rg-flag.men'), voce = pos('.rg-flag.you');
    if (!men) return;
    const ini = Math.min(men.l, voce?.l ?? men.l), fim = Math.max(men.r, voce?.r ?? men.r);
    const alvo = fim - ini <= wrap.clientWidth ? Math.min(ini, fim - wrap.clientWidth)
      : voce && voce.l < men.l ? men.r - wrap.clientWidth : men.l;
    wrap.scrollLeft = Math.max(0, alvo);
  }, [aba, serie, perfil]);

  // As abas grudam logo abaixo do topo (syncTopH do protótipo).
  useLayoutEffect(() => {
    const raiz = raizRef.current;
    if (!raiz) return;
    const medir = () => {
      raiz.style.setProperty('--topH', `${topoRef.current?.offsetHeight || 56}px`);
      raiz.style.setProperty('--navH', `${abasRef.current?.offsetHeight || 0}px`);
    };
    medir();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medir) : null;
    if (ro && topoRef.current) ro.observe(topoRef.current);
    return () => ro?.disconnect();
  }, [perfil]);

  const eps: Ep[] = useMemo(() => serie?.episodios || [], [serie]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  // Troca a aba pelo endereço (?aba=; a primeira aba ligada é o endereço sem ?aba).
  const trocarAba = useCallback((a: Aba, replace = false) => {
    const sp = new URLSearchParams(location.search);
    if (a === abaInicial) sp.delete('aba'); else sp.set('aba', a);
    const q = sp.toString();
    navigate({ pathname: location.pathname, search: q ? `?${q}` : '' }, { replace });
  }, [location.pathname, location.search, navigate, abaInicial]);

  // Rola até o começo da aba, logo abaixo das abas presas: nem o topo da
  // página, nem o conteúdo escondido atrás do cabeçalho.
  const rolarAteAba = useCallback(() => {
    const raiz = raizRef.current, painel = painelRef.current;
    if (!raiz || !painel) return;
    const presas = (topoRef.current?.offsetHeight || 0) + (abasRef.current?.offsetHeight || 0);
    const alvo = Math.max(0, Math.round(painel.getBoundingClientRect().top - raiz.getBoundingClientRect().top + raiz.scrollTop - presas));
    const calmo = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    raiz.scrollTo({ top: alvo, behavior: calmo ? 'auto' : 'smooth' });
  }, []);

  // A aba mudou (toque, atalho do mapa ou o voltar do navegador): fecha a
  // folha e rola até ela. Na primeira abertura, inclusive por ?aba=, a página
  // começa do topo.
  const abaAnterior = useRef(aba);
  useLayoutEffect(() => {
    if (abaAnterior.current === aba) return;
    abaAnterior.current = aba;
    setFolha(null);
    rolarAteAba();
  }, [aba, rolarAteAba]);

  // De volta do cadastro/login: abre o passo que a pessoa tinha pedido (uma vez).
  useEffect(() => {
    if (!abrirAoEntrar || !session || !perfil || perfil.vitrine) return;
    if (abrirAoEntrar === 'assinar') {
      if (!perfil.tem_acesso && !perfil.sou_o_mentor) setFolha({ tipo: 'assinar' });
    } else if (abrirAoEntrar !== 'pagina') {
      setMapaVisto(true);
      trocarAba(abrirAoEntrar === 'clube' ? 'clube' : 'cadernos', true);
    }
    onAbriuAoEntrar?.();
  }, [abrirAoEntrar, session, perfil, onAbriuAoEntrar]); // eslint-disable-line react-hooks/exhaustive-deps

  if (erro || !perfil) {
    return (
      <div className="cm cm-pagina" ref={raizRef}>
        <header className="top solid" ref={topoRef}>
          {!vitrine && !previa && <button className="cm-voltar" onClick={onBack} aria-label="Voltar">{Ic.left}</button>}
          <div className="brand"><b>Forjamente</b><span>Círculo</span></div>
        </header>
        <section className="sec cm-aviso"><span className="eyebrow">{erro || 'Abrindo o Círculo…'}</span>
          {erro && <div className="btns" style={{ marginTop: 14 }}><button className="btn btn-2 btn-sm" onClick={carregar}>Tentar de novo</button></div>}
        </section>
      </div>
    );
  }

  const m = perfil.mentor;
  const nome1 = primeiroNome(m.nome);
  const resto = String(m.nome || '').trim().slice(nome1.length).trim();
  // Na prévia o Mentor se vê como o assinante: sem os botões de dono.
  const souOMentor = !!perfil.sou_o_mentor && !previa;
  const entrar = onEntrar ? (acao: AcaoAoEntrar, modo: 'register' | 'login') => onEntrar(acao, modo, m.nome) : undefined;
  const temAcesso = !!perfil.tem_acesso || previa;
  const visitante = !temAcesso && !souOMentor;
  // Sem série publicada (publicando ou concluída) não há o que vender: nada de
  // "Assinar" nem de mapa, e sim um aviso curto; o Mentor ganha o atalho para
  // publicar (QA final, D2). O rascunho, só o Mentor vê, e não conta.
  const semSerie = !previa && !(perfil.series || []).some((s: any) => s.status === 'publicando' || s.status === 'concluida');
  const mostraMapa = visitante && !semSerie && !mapaVisto && !veioComAba && secoes.some((s) => s.key === 'mapa' && s.on);
  const sa = perfil.serie_atual;
  const livro = sa ? { titulo: sa.livro.titulo, autor: sa.livro.autor, paginas: sa.livro.paginas || 0, foto: sa.capa_url || null } : null;
  const prox = proximoEp(eps);
  const noAr = eps.filter(publicado).length;
  // Só se promete clube (e "grátis") quando o Mentor tem um (QA #2).
  const oClube = clube?.clube || null;
  const clubeAberto = !!oClube?.aberto;
  // Livro e série com o mesmo título: o título do livro não se repete (QA #10).
  const mesmoTituloDaSerie = !!sa && normTitulo(sa.titulo) === normTitulo(sa.livro.titulo);

  // A mesma aba de novo só rola até o começo dela; outra aba entra no
  // histórico e o efeito acima rola.
  const irPara = (a: Aba) => {
    setFolha(null);
    if (a === aba) rolarAteAba(); else trocarAba(a);
  };
  const tocarMapa = (a: Aba) => {
    setMapaVisto(true);
    if (session) api.setTutorialState(session, 'circulo_mapa', { seen: true }).catch(() => {});
    irPara(a);
  };
  const abrirEp = (e: Ep | null) => {
    if (!e) return;
    if (visitante && publicado(e)) { setFolha({ tipo: 'assinar' }); return; }
    if (e.pode_assistir) { setFolha({ tipo: 'ep', id: e.id }); return; }
    // O que ainda não abre responde ao toque com o motivo (QA #12).
    const st = epState(e, temAcesso);
    setToast(e.estado === 'em_producao' ? 'Sai em breve' : st.txt.charAt(0).toUpperCase() + st.txt.slice(1));
  };

  /* ----- topo compacto (heroIdentity) ----- */
  const desde = perfil.assinatura?.desde ? new Date(perfil.assinatura.desde) : null;
  const eyebrowTopo = souOMentor ? 'o seu Círculo'
    : perfil.assinante || previa ? (desde ? `você assina desde ${MESES[desde.getMonth()]}` : 'você assina')
      : 'assinatura do mentor';
  // Só os números maiores que zero ("0 dias seguidos" não diz nada a ninguém).
  const ano = perfil.ano || {};
  const topo = (
    <section className="mh compact">
      <div className="mh-l">
        <span className="eyebrow">Círculo de leitura · {eyebrowTopo}</span>
        <h1 className="mh-name">{nome1}{resto && <><br /><em>{resto}</em></>}</h1>
        {m.tagline && <p className="mh-frase" data-frase="">{m.tagline}</p>}
        {(m.bio || m.manifesto) && <p className="mh-man">{m.bio || m.manifesto}</p>}
        {(ano.livros_lidos > 0 || ano.paginas > 0 || ano.dias_seguidos > 0) && (
          <div className="mstats" data-numeros="">
            {ano.livros_lidos > 0 && <span data-n="livros"><b>{fmtN(ano.livros_lidos)}</b> {ano.livros_lidos === 1 ? 'livro lido' : 'livros lidos'} em {ano.ano}</span>}
            {ano.paginas > 0 && <span data-n="paginas"><b>{fmtN(ano.paginas)}</b> {ano.paginas === 1 ? 'página' : 'páginas'} em {ano.ano}</span>}
            {ano.dias_seguidos > 0 && <span data-n="dias"><b>{fmtN(ano.dias_seguidos)}</b> {ano.dias_seguidos === 1 ? 'dia seguido' : 'dias seguidos'}</span>}
          </div>
        )}
        {semSerie && (visitante || souOMentor) && (
          <p className="cm-topo-vazio" data-sem-serie={souOMentor ? 'mentor' : 'visitante'}>
            {!souOMentor ? `A primeira série de ${nome1} está chegando. A assinatura abre quando ela for publicada.`
              : sa ? 'Sua série ainda está em rascunho. Enquanto isso, a página não mostra a assinatura.'
                : 'Você ainda não tem uma série. Enquanto isso, a página não mostra a assinatura.'}
          </p>
        )}
        <div className="btns">
          {visitante ? (
            !semSerie && <button className="btn btn-ember" data-cta="assinar" onClick={() => setFolha({ tipo: 'assinar' })}>Assinar · {perfil.preco?.mensal?.texto}</button>
          ) : (
            <>
              {souOMentor && semSerie && (onAbrirSerieNoEditor || onAbrirEditor)
                ? <button className="btn btn-1" data-cta="publicar-serie" onClick={onAbrirSerieNoEditor || onAbrirEditor}>{sa ? 'Publicar a série' : 'Criar a série'}</button>
                : prox
                  ? <button className="btn btn-1" data-cta="continuar" onClick={() => abrirEp(prox)}>{Ic.play}Continuar ep. {prox.posicao}</button>
                  : eps.length && temAba('conteudo')
                    ? <button className="btn btn-1" data-cta="episodios" onClick={() => irPara('conteudo')}>{Ic.play}Ver os episódios</button>
                    // Nenhum episódio ainda (QA #11): diz isso em vez de um botão para o nada.
                    : <p className="cm-topo-vazio" data-topo-vazio="">{souOMentor ? 'Nenhum episódio ainda. Suba o primeiro em "Meus episódios".' : `Os episódios aparecem aqui quando ${nome1} publicar.`}</p>}
              {souOMentor && onAbrirEstudio
                ? <button className="btn btn-2" data-cta="estudio" onClick={onAbrirEstudio}>Meus episódios</button>
                : oClube && temAba('clube') && <button className="btn btn-2" data-cta="ver-clube" onClick={() => irPara('clube')}>{Ic.users}Ver o clube</button>}
              {souOMentor && onAbrirEditor && <button className="btn btn-2" data-cta="editor" onClick={onAbrirEditor}>Editar meu Círculo</button>}
            </>
          )}
        </div>
      </div>
    </section>
  );

  /* ----- Estante (modelo): as séries do Mentor como lombadas, logo abaixo do topo ----- */
  // Só tokens: cada lombada pega um par (fundo, letra) da paleta escolhida.
  const layout: string = pagina?.layout || 'editorial';
  const series: any[] = (perfil.series || []).filter((s: any) => s.status !== 'rascunho' || souOMentor);
  const LOMBADAS = [['var(--ember)', 'var(--on-ember)'], ['var(--sage)', 'var(--paper)'], ['var(--ink-2)', 'var(--paper)'], ['var(--paper-3)', 'var(--ink)']];
  const tocarLombada = (s: any) => {
    if (sa && s.id === sa.id && temAba('conteudo')) { irPara('conteudo'); return; }
    setToast(s.status === 'concluida' ? `${s.titulo} · série concluída` : `${s.titulo} · em breve`);
  };
  const estante = layout === 'estante' && series.length > 0 && (
    <section className="sec cm-estante" data-estante="">
      <div className="sec-head"><div><span className="eyebrow">A estante</span><h2>As séries de {nome1}</h2></div></div>
      <div className="shelf-wrap"><div className="shelf">
        {series.map((s: any, i: number) => {
          const [c, f] = LOMBADAS[i % LOMBADAS.length];
          const rotulo = s.livro || s.titulo;
          const w = Math.max(26, Math.min(40, 18 + String(rotulo).length));
          const h = 150 + ((i * 37) % 46);
          return (
            <button key={s.id} className={`spine${sa && s.id === sa.id ? ' now' : ''}`} data-lombada={s.id}
              style={{ '--c': c, '--f': f, '--w': `${w}px`, '--h': `${h}px` } as React.CSSProperties}
              aria-label={`${s.titulo}${s.autor ? `, ${s.autor}` : ''}`} onClick={() => tocarLombada(s)}><span>{rotulo}</span></button>
          );
        })}
      </div></div>
      <div className="shelf-cap">
        <span>{sa ? <>Agora: <b>{sa.titulo}</b></> : null}</span>
        <span>{series.length === 1 ? '1 série' : `${series.length} séries`}</span>
      </div>
    </section>
  );

  /* ----- mapa de abertura (mapaIntro) ----- */
  // O cartão do Clube só entra se houver clube; "grátis" só se for aberto (QA #2).
  // Espera a resposta do clube para não mudar de 3 para 4 cartões na frente da pessoa.
  const cartoesDoMapa = ([
    ['conteudo', Ic.book, 'Conteúdo', 'Episódios em vídeo por página, sem spoiler'],
    oClube ? ['clube', Ic.users, 'Clube', `A conversa de quem lê junto${clubeAberto ? ' · grátis' : ''}`] : null,
    ['estudo', Ic.layers, 'Estudo', oClube ? 'A leitura meta por meta, com o clube' : 'A leitura meta por meta'],
    ['cadernos', Ic.pencil, 'Cadernos', 'Suas notas · pessoais e grátis'],
  ] as ([Aba, React.ReactNode, string, string] | null)[]).filter((c) => c && temAba(c[0])) as [Aba, React.ReactNode, string, string][];
  // Um lugar só não é mapa: com menos de dois cartões, ele não aparece.
  const mapa = mostraMapa && cartoesDoMapa.length > 1 && (clube !== undefined || clubeErro) && (
    <section className="sec mapa-intro" data-mapa="">
      <div className="sec-head"><div><span className="eyebrow">Antes de começar</span><h2>Um mapa do Círculo</h2></div>
        <p>{NUM_LUGARES[cartoesDoMapa.length]} lugares, um propósito cada. Toque para ir direto.</p></div>
      <div className="mapa-grid">
        {cartoesDoMapa.map(([k, ic, t, d]) => (
          <button key={k} className="mapa-it" data-mapa-it={k} onClick={() => tocarMapa(k)}>
            <span className="mapa-ic">{ic}</span><h4>{t}</h4><p>{d}</p>
          </button>
        ))}
      </div>
    </section>
  );

  /* ----- abas presas (abasBar) ----- */
  const abas = (
    <div className="ctabs-wrap" ref={abasRef} data-abas="">
      <nav className="tabs" role="tablist" aria-label="Seções do Círculo">
        {abasVisiveis.map(([k, l]) => (
          <button key={k} className="tab" role="tab" aria-current={aba === k ? 'page' : 'false'} data-aba={k} onClick={() => irPara(k)}>{l}</button>
        ))}
      </nav>
    </div>
  );

  /* ----- "Na mesa agora" (heroMesa): o livro da série, só na aba Conteúdo ----- */
  const lendo = perfil.mentor_lendo;
  const pctMesa = lendo && lendo.total ? Math.min(100, Math.round(lendo.pagina / lendo.total * 100)) : 0;
  // Quem ainda não começou o livro: "Ainda não começou" no lugar de "0%" e
  // "p. 0 de N", que não dizem nada (QA #11; QA final, D7).
  const naoComecou = !(lendo?.pagina > 0);
  const mesa = lendo && livro && (
    <div className="mh-mesa-wrap compact">
      <aside className="mesa compact" style={capaVars} data-mesa="">
        <div className="hero-bg" />
        <span className="eyebrow"><i />Na mesa agora</span>
        <div className="mesa-book"><Livro3d titulo={livro.titulo} autor={livro.autor} paginas={livro.paginas} foto={livro.foto} w="clamp(88px, 9vw, 130px)" /></div>
        <div className="mesa-info">
          {!naoComecou && <div className="ring" style={{ '--p': pctMesa } as React.CSSProperties}><b>{pctMesa}%</b></div>}
          <div><h4>{livro.titulo}</h4><p>{livro.autor}</p>
            {naoComecou ? <p className="cm-nao-comecou" data-nao-comecou="">Ainda não começou</p>
              : <p className="mono">p. {fmtN(lendo.pagina)} de {fmtN(lendo.total)}</p>}</div>
        </div>
      </aside>
    </div>
  );

  /* ----- aba Conteúdo (serieSpread + regua + sumario) ----- */
  const conteudo = () => {
    if (!sa || !serie || !livro) {
      return (
        <section className="sec" id="serie">
          <div className="sec-head"><div><span className="eyebrow">A série</span><h2>Em breve</h2></div>
            <p>A primeira série de {nome1} está sendo preparada.</p></div>
        </section>
      );
    }
    const P = livro.paginas || 1;
    const voce = serie.pagina_do_leitor || 0;
    const men = lendo?.pagina || 0;
    const destaque = (visitante ? eps.find(publicado) : prox) || eps[0] || null;

    // Bandeira perto de uma ponta da régua: presa a ela, para não sair cortada.
    const borda = (f: number) => (f < 0.06 ? ' inicio' : f > 0.94 ? ' fim' : '');
    const ticks: React.ReactNode[] = [];
    for (let p = 0; p <= P; p += 25) {
      ticks.push(<i key={`t${p}`} className={`rg-t ${p % 50 === 0 ? 'big' : ''}`} style={{ left: `${p / P * 100}%` }} />);
      if (p % 50 === 0 && Math.abs(p - voce) > 14) ticks.push(<span key={`l${p}`} className="rg-l" style={{ left: `${p / P * 100}%` }}>{p}</span>);
    }

    let feat: React.ReactNode = null;
    if (destaque) {
      const st = epState(destaque, temAcesso);
      const i = eps.indexOf(destaque);
      // "para quem assina" só quando o episódio está no ar (QA #3).
      const eye = visitante && publicado(destaque) ? `Ep. ${destaque.posicao} · para quem assina`
        : !st.open ? `Ep. ${destaque.posicao} · ${st.txt}`
          : `Ep. ${destaque.posicao} · ${destaque.segundos_assistidos ? 'continue de onde parou' : 'próximo para você'}`;
      const quadro = <Quadro livro={livro} roman={ROM[i] || String(destaque.posicao)} eye={eye} title={destaque.titulo}
        dur={minutos(destaque.duracao_s)} prog={st.open ? pctVisto(destaque) : undefined} dim={!st.open && !(visitante && publicado(destaque))} />;
      feat = (
        <>
          <button className="card" data-feat="" onClick={() => abrirEp(destaque)} aria-label={`Episódio ${destaque.posicao}, ${st.txt}`}><div className="art">{quadro}</div></button>
          <div className="sp-fm"><span>{visitante
            ? `${noAr ? 'Os episódios são em vídeo, para quem assina.' : 'Os episódios são em vídeo, para quem assina, e ainda estão em produção.'}${clubeAberto ? ' O clube continua grátis.' : ''}`
            : destaque.sinopse || (st.open ? '' : `O episódio ${destaque.posicao} ${st.txt}.`)}</span></div>
        </>
      );
    }

    return (
      <section className="sec" id="serie">
        <div className="sec-head">
          <div><span className="eyebrow">A série em vídeo · {eps.length ? <><b>{noAr} de {eps.length}</b> {eps.length === 1 ? 'episódio' : 'episódios'} no ar</> : 'nenhum episódio ainda'}{sa.cadencia ? ` · ${sa.cadencia}` : ''}</span><h2>{sa.titulo}</h2></div>
          <div>
            <p>{sa.descricao ? `${sa.descricao} ` : ''}A linha abaixo é o próprio livro: cada número é um episódio, na página em que ele começa.</p>
            <div className="btns" style={{ marginTop: 14 }}>
              <button className="btn btn-2 btn-sm" data-ver-serie="" onClick={() => setFolha({ tipo: 'serie' })}>Ver a série completa</button>
              {sa.amazon_url && <a className="btn btn-2 btn-sm buy-book" data-comprar="" href={sa.amazon_url} target="_blank" rel="noopener noreferrer">{Ic.book}Comprar o livro</a>}
            </div>
          </div>
        </div>

        <div className="rg-wrap" ref={reguaRef}><div className="regua" role="img" aria-label={`Linha do livro de ${P} páginas com os episódios`}>
          <div className="rg-track" /><div className="rg-fill men" style={{ width: `${men / P * 100}%` }} /><div className="rg-fill you" style={{ width: `${voce / P * 100}%` }} />
          {ticks}
          {eps.map((e) => {
            const st = epState(e, temAcesso);
            return (
              <button key={e.id} className={`rg-m ${st.cls}`} style={{ left: `${Math.max(e.abre_na_pagina, 1) / P * 100}%` }}
                tabIndex={st.open ? 0 : -1} onClick={() => abrirEp(e)}
                title={`Ep. ${e.posicao}: ${e.titulo} (${st.txt})`} aria-label={`Episódio ${e.posicao}, ${e.titulo}, ${st.txt}`}>{e.posicao}</button>
            );
          })}
          {(men > 0 || !visitante) && <div className={`rg-flag men${borda(men / P)}`} style={{ left: `${men / P * 100}%` }}><span>{nome1} · {men > 0 ? `p. ${men}` : 'ainda não começou'}</span></div>}
          {voce > 0 && !souOMentor && <div className={`rg-flag you${borda(voce / P)}`} style={{ left: `${voce / P * 100}%` }}><span>Você · p. {voce}</span></div>}
        </div></div>
        {reguaRola && eps.length > 0 && <p className="rg-hint" data-rg-hint="">Arraste para o lado para ver todos os episódios</p>}
        {eps.length > 0 && <div className="rg-leg">
          <span><i style={{ background: 'var(--sage)' }} />assistido</span><span><i style={{ background: 'var(--ink)' }} />liberado</span>
          <span><i style={{ background: 'var(--paper-3)', border: '1px solid var(--line-strong)' }} />abre pela página</span>
          <span><i style={{ border: '1.5px dashed var(--ember)' }} />estreia marcada</span><span><i style={{ border: '1.5px dashed var(--ink-4)' }} />em produção</span>
        </div>}

        {!eps.length ? (
          <div className="club" data-eps-vazio="">
            <h4>{souOMentor ? 'Nenhum episódio ainda' : `${nome1} ainda não publicou episódios`}</h4>
            <p>{souOMentor ? 'Os episódios que você subir em "Meus episódios" aparecem aqui, cada um na página que ele comenta.' : 'Cada episódio aparece aqui, na página do livro que ele comenta.'}</p>
          </div>
        ) : <div className="sp-cols">
          <div className="sp-feat">{feat}</div>
          <ol className="sumario">
            {eps.map((e, i) => {
              const st = epState(e, temAcesso);
              return (
                <li key={e.id} className={st.open ? '' : 'dim'} data-ep={e.posicao} data-estado={st.cls}
                  role="button" tabIndex={0} onClick={() => abrirEp(e)} onKeyDown={(ev: React.KeyboardEvent) => { if (ev.key === 'Enter') abrirEp(e); }}>
                  <span className="n">{ROM[i] || e.posicao}</span>
                  <div className="ln"><span className="t">{e.titulo}</span><span className="dots" /><span className="pg">{e.abre_na_pagina > 0 ? `p. ${e.abre_na_pagina}` : 'antes da 1ª página'}</span></div>
                  <span className={`st ${st.st}`}>{(st.open || (visitante && publicado(e))) && e.duracao_s ? `${minutos(e.duracao_s)} · ` : ''}{st.txt}</span>
                </li>
              );
            })}
          </ol>
        </div>}
      </section>
    );
  };

  /* ----- Clube, Estudo, Cadernos (P2: mentor-abas.tsx) ----- */
  const painel = aba === 'conteudo' ? <div id="conteudo">{mesa}{conteudo()}</div>
    : aba === 'clube' ? <AbaClube session={session} nome1={nome1} dados={clube} erro={clubeErro} onRecarregar={carregarClube}
      onAbrirClube={onAbrirClube} onAbrirClubes={souOMentor ? onAbrirClubes : undefined}
      onEntrar={entrar ? (modo) => entrar('clube', modo) : undefined} />
      : aba === 'estudo' ? <AbaEstudo nome1={nome1} dados={clube} erro={clubeErro} paginaDoLeitor={serie?.pagina_do_leitor || 0}
        onRecarregar={carregarClube} onIrParaClube={() => irPara('clube')} onAbrirEstudo={onAbrirEstudoDoClube} />
        : <AbaCadernos session={session} slug={slug} nome1={nome1} dados={caderno} erro={cadernoErro} onRecarregar={carregarCaderno}
          onMudou={setCaderno} onAbrirCaderno={onAbrirCadernoDoLivro} onAbrirCadernos={onAbrirCadernos}
          onEntrar={entrar ? (modo) => entrar('caderno', modo) : undefined} />;

  /* ----- folhas ----- */
  const epAberto = folha?.tipo === 'ep' ? eps.find((e) => e.id === folha.id) || null : null;
  const fechar = () => setFolha(null);

  return (
    <div className="cm cm-pagina" ref={raizRef} data-papel={souOMentor ? 'mentor' : visitante ? 'visitante' : 'assinante'}
      data-layout={layout} data-paleta={pagina?.paleta || 'midnight'}
      {...(vitrine ? { 'data-vitrine': '' } : {})} {...(previa ? { 'data-previa': '' } : {})}>
      <header className="top solid" id="top" ref={topoRef}>
        {!vitrine && !previa && <button className="cm-voltar" onClick={onBack} aria-label="Voltar">{Ic.left}</button>}
        <div className="brand"><b>Forjamente</b><span>Círculo</span></div>
        {vitrine && entrar && <button className="btn btn-2 btn-sm cm-entrar" data-cta="entrar" onClick={() => entrar('pagina', 'login')}>Entrar</button>}
      </header>
      <main>
        {topo}
        {estante}
        {mapa}
        {abas}
        <div className="abas-panel" ref={painelRef} data-painel={aba}>{painel}</div>
      </main>
      {toast && <div className="toast" role="status" data-toast="">{toast}</div>}

      {folha?.tipo === 'assinar' && !semSerie && (
        <Folha onFechar={fechar} estreita>
          <div className="sh-body">
            <div><span className="eyebrow">Círculo · assinatura do mentor</span>
              <h2 className="serif" style={{ margin: '6px 0 4px', fontSize: 'clamp(32px,3.6vw,46px)', lineHeight: 1, fontWeight: 400 }}>Leia junto com {nome1 === 'Wesley' ? 'o Wesley' : nome1}</h2>
              {/* O mensal e o anual, do precoDoMentor (QA #3). */}
              {perfil.preco?.mensal?.texto && <p className="cm-precos" data-precos="">{perfil.preco.mensal.texto}{perfil.preco.anual?.texto ? <> ou <b>{perfil.preco.anual.texto}</b></> : null}</p>}
              {/* "grátis" só com clube aberto de verdade (QA #2); o "acesso a tudo" fica só na lista (QA #10). */}
              {clubeAberto && <p style={{ margin: 0, color: 'var(--ink-2)' }} data-folha-clube="">O clube de {nome1} continua grátis e aberto para todos.</p>}</div>
            <ul className="pw-list">
              {/* A série pelo que está no ar, não pelo que foi planejado (QA #3). */}
              {sa && <li data-folha-serie="">{Ic.check}<span><b style={{ color: 'var(--ink)' }}>A série <i>{sa.titulo}</i></b> · em vídeo · {noAr ? `${noAr} ${noAr === 1 ? 'episódio' : 'episódios'} no ar` : (sa.cadencia || 'os episódios estão em produção')}. Cada um abre na página em que {nome1} o comenta, sem spoiler</span></li>}
              <li>{Ic.check}<span>Acesso a tudo o que {nome1} publicar no Círculo, uma assinatura só</span></li>
              <li>{Ic.check}<span>O app do Forjamente continua grátis para você; a assinatura é do conteúdo de {nome1}</span></li>
            </ul>
            {vitrine && !isNativeApp() ? (
              // Sem conta, na web: a compra é só pelo app — avisa ANTES do cadastro (QA #1).
              // A conta é grátis e o retorno pendente volta a esta página depois.
              <div className="btns" data-assinar-sem-conta="" data-assinar-pelo-app="">
                <p className="cm-pelo-app" data-aviso-app="">A assinatura é feita no app. Crie a conta grátis aqui e assine pelo app em 1 toque.</p>
                <button className="btn btn-ember" data-cta="criar-conta" onClick={() => entrar?.('assinar', 'register')}>Criar conta grátis</button>
                <button className="btn btn-2" data-cta="ja-tenho-conta" onClick={() => entrar?.('assinar', 'login')}>Já tenho conta · Entrar</button>
              </div>
            ) : vitrine ? (
              // Sem conta, no app: a conta primeiro; depois volta direto para a compra.
              <div className="btns" data-assinar-sem-conta="">
                <button className="btn btn-ember" data-cta="criar-conta" onClick={() => entrar?.('assinar', 'register')}>Criar conta para assinar · {perfil.preco?.mensal?.texto}</button>
                <button className="btn btn-2" data-cta="ja-tenho-conta" onClick={() => entrar?.('assinar', 'login')}>Já tenho conta · Entrar</button>
              </div>
            ) : (
              <AssinarMentor session={session} slug={slug} mentorNome={m.nome} compra={perfil.compra}
                precos={{ mensal: perfil.preco?.mensal?.texto, anual: perfil.preco?.anual?.texto }}
                onAssinou={() => { fechar(); carregar(); }} />
            )}
          </div>
        </Folha>
      )}

      {folha?.tipo === 'serie' && sa && livro && (
        <Folha onFechar={fechar}>
          <div className="sh-body">
            <div><span className="eyebrow">Série em vídeo · {eps.length} {eps.length === 1 ? 'episódio' : 'episódios'}</span>
              <h2 className="serif" style={{ margin: '6px 0 0', fontSize: 34, lineHeight: 1.04, fontWeight: 400 }}>{sa.titulo}</h2></div>
            <div><div className="eps-h"><h3>Episódios</h3><span>{eps.filter(publicado).length} de {eps.length} no ar</span></div>
              <div id="eps">
                {eps.map((e) => {
                  const st = epState(e, temAcesso);
                  const faltam = Math.max(0, e.abre_na_pagina - (serie?.pagina_do_leitor || 0));
                  const gate = !publicado(e) ? <div className="gate">{e.estado === 'estreia' ? e.texto : 'Em produção'}</div>
                    : visitante ? <div className="gate">{Ic.lockP}Assine o Círculo para destrancar<button onClick={() => setFolha({ tipo: 'assinar' })}>Assinar</button></div>
                      : !st.open ? <div className="gate">{Ic.lockP}Abre na página {e.abre_na_pagina} · faltam {faltam} páginas</div> : null;
                  return (
                    <div key={e.id} className={`ep ${e === prox ? 'cur' : ''} ${st.open ? '' : 'locked'}`}
                      {...(st.open ? { role: 'button', tabIndex: 0, onClick: () => abrirEp(e) } : {})}>
                      <span className="num">{e.posicao}</span>
                      <div style={{ position: 'relative' }}>
                        <Quadro livro={livro} eye={mesmoTituloDaSerie ? `Ep. ${e.posicao}` : `Ep. ${e.posicao} · ${livro.titulo}`} title={e.titulo} dur={minutos(e.duracao_s)}
                          dim={!st.open} prog={st.open ? (e.estado === 'assistido' ? 100 : pctVisto(e)) : undefined} />
                        {!st.open && publicado(e) && <span className="lockb">{Ic.lock}</span>}
                      </div>
                      <div><h5>{e.titulo}{e.duracao_s ? <span>{minutos(e.duracao_s)}</span> : null}</h5>
                        {e.sinopse && <p>{e.sinopse}</p>}
                        {e.estado === 'assistido' && st.open && <div className="gate seen">Assistido</div>}
                        {gate}</div>
                    </div>
                  );
                })}
              </div></div>
          </div>
        </Folha>
      )}

      {epAberto && livro && (
        <Folha onFechar={fechar}>
          <Player key={epAberto.id} session={session} slug={slug} ep={epAberto} livro={livro}
            proximo={eps[eps.indexOf(epAberto) + 1]?.pode_assistir ? eps[eps.indexOf(epAberto) + 1] : null}
            onProximo={(e) => setFolha({ tipo: 'ep', id: e.id })}
            onMudou={carregar} />
        </Folha>
      )}
    </div>
  );
};

/* ===== folha (.ov > .sheet) ===== */
const Folha: React.FC<{ onFechar: () => void; estreita?: boolean; children: React.ReactNode }> = ({ onFechar, estreita, children }) => {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onFechar]);
  return (
    <div className="ov" onClick={(e) => { if (e.target === e.currentTarget) onFechar(); }}>
      <div className={`sheet ${estreita ? 'narrow' : ''}`} role="dialog" aria-modal="true">
        <button className="x" onClick={onFechar} aria-label="Fechar">{Ic.x}</button>
        {children}
      </div>
    </div>
  );
};

/* ===== player: link assinado da API, retoma de onde parou e salva o progresso ===== */
const SALVAR_A_CADA = 10; // segundos de vídeo
const Player: React.FC<{
  session: any; slug: string; ep: Ep; livro: { titulo: string; autor: string };
  proximo: Ep | null; onProximo: (e: Ep) => void; onMudou: () => void;
}> = ({ session, slug, ep, livro, proximo, onProximo, onMudou }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [assistido, setAssistido] = useState(ep.estado === 'assistido');
  const [tam, setTam] = useState<null | { w: number; h: number }>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const salvo = useRef({ seg: ep.segundos_assistidos || 0, concluido: ep.estado === 'assistido', mudou: false });
  const tocou = useRef(false);
  const aoTocar = () => {
    if (tocou.current) return;
    tocou.current = true;
    usoAcao('episodio_tocar', { slug, ep: ep.id, posicao: ep.posicao, de_s: Math.floor(videoRef.current?.currentTime || 0) });
  };

  useEffect(() => {
    let vivo = true;
    setSrc(null); setAviso(null);
    api.getEpisodioVideo(session, slug, ep.id)
      .then((r) => {
        if (!vivo) return;
        if (r.largura && r.altura) setTam({ w: r.largura, h: r.altura });
        setSrc(r.caminho || r.url);
      })
      .catch((e: any) => { if (vivo) setAviso(e?.message || 'Não deu para abrir o vídeo agora.'); });
    return () => { vivo = false; };
  }, [session, slug, ep.id]);

  const salvar = useCallback((concluido = false) => {
    const v = videoRef.current;
    const seg = Math.floor(v ? v.currentTime : salvo.current.seg);
    const fim = concluido && !salvo.current.concluido;
    if (!fim && Math.abs(seg - salvo.current.seg) < 1) return;
    salvo.current = { seg, concluido: salvo.current.concluido || concluido, mudou: true };
    api.salvarProgressoEpisodio(session, slug, ep.id, seg, concluido).catch(() => {});
  }, [session, slug, ep.id]);

  // Ao fechar a folha: grava onde parou e recarrega os estados da página.
  useEffect(() => () => {
    salvar(false);
    if (salvo.current.mudou) onMudou();
  }, [salvar]); // eslint-disable-line react-hooks/exhaustive-deps

  const aoTempo = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    if (!salvo.current.concluido && v.currentTime / v.duration >= 0.9) { salvar(true); setAssistido(true); return; }
    if (Math.abs(v.currentTime - salvo.current.seg) >= SALVAR_A_CADA) salvar(false);
  };
  const aoCarregar = () => {
    const v = videoRef.current;
    const s = ep.segundos_assistidos || 0;
    if (v && s > 0 && v.duration && s < v.duration - 5) v.currentTime = s;
    if (v && v.videoWidth && v.videoHeight && (v.videoWidth !== tam?.w || v.videoHeight !== tam?.h)) {
      setTam({ w: v.videoWidth, h: v.videoHeight });
    }
  };

  // Em pé ou deitado: a proporção vem com o link (o servidor lê o arquivo) e o
  // loadedmetadata confirma. Em pé, a caixa segue o vídeo (QA final, D1).
  const forma = !tam ? undefined : tam.h > tam.w ? 'vertical' : 'paisagem';
  const estilo = tam ? { '--cm-ar': `${tam.w} / ${tam.h}`, '--cm-w': `${tam.w}px` } as React.CSSProperties : undefined;

  return (
    <>
      <div className="player cm-player" id="player" data-forma={forma} style={estilo}>
        {src ? (
          <video ref={videoRef} className="cm-video" src={src} controls playsInline preload="metadata"
            onLoadedMetadata={aoCarregar} onTimeUpdate={aoTempo} onPlay={aoTocar} onPause={() => salvar(false)}
            onEnded={() => { salvar(true); setAssistido(true); }} />
        ) : aviso ? (
          <>
            <Quadro livro={livro} eye={`Ep. ${ep.posicao}`} title={ep.titulo} />
            <div className="cm-player-aviso">{aviso}</div>
          </>
        ) : (
          <div className="cm-video-espera">Carregando o vídeo…</div>
        )}
      </div>
      <div className="sh-body">
        <div><span className="eyebrow">{livro.titulo} · episódio {ep.posicao}</span>
          <h2 className="serif" style={{ margin: '6px 0 8px', fontSize: 'clamp(28px,3vw,40px)', lineHeight: 1.04, fontWeight: 400 }}>{ep.titulo}</h2>
          {ep.sinopse && <p style={{ margin: 0, color: 'var(--ink-2)' }}>{ep.sinopse}</p>}</div>
        <div className="btns">
          <button className="btn btn-1 btn-sm" data-marcar="" disabled={assistido}
            onClick={() => { salvar(true); setAssistido(true); }}>{Ic.check}{assistido ? 'Assistido' : 'Marcar como assistido'}</button>
          {proximo && <button className="btn btn-2 btn-sm" onClick={() => onProximo(proximo)}>Próximo episódio {Ic.right}</button>}
        </div>
      </div>
    </>
  );
};
